import { rfqIdForUrl } from './rfqIdentity';

/**
 * Resource-style wizard URLs: `/rfqs/:rfqId/<step>`, where `:rfqId` is the
 * human RFQ number in its DISPLAY form — the stored id's org-code suffix is
 * stripped by `rfqIdForUrl`, so what a person sees in the address bar is
 * `RFQ-003` (the backend resolver accepts that form permanently).
 * The URL is the single source of truth for the wizard position — refresh,
 * share, and browser back/forward all work from it alone. Navigation never
 * creates records: there is deliberately no `/rfqs/new` route, because a
 * create-on-GET URL would spawn empty drafts on refresh/prefetch.
 */
export interface WizardPos {
  step: number;
  subStep: number;
}

export interface WizardStepRoute {
  /** Path pattern under `/rfqs/:rfqId/`. */
  path: string;
  pos: WizardPos;
}

/**
 * Live steps only. Locked stages (Financing, Delivery, Installation,
 * Contracting) get NO routes until they are real.
 */
export const WIZARD_STEP_ROUTES: WizardStepRoute[] = [
  { path: 'upload', pos: { step: 1, subStep: 1 } },
  { path: 'criteria', pos: { step: 2, subStep: 1 } },
  { path: 'discovery/context', pos: { step: 3, subStep: 1 } },
  { path: 'discovery/validation', pos: { step: 3, subStep: 2 } },
  { path: 'discovery', pos: { step: 3, subStep: 3 } },
  { path: 'outreach/:threadId?', pos: { step: 4, subStep: 1 } },
  { path: 'quotes', pos: { step: 5, subStep: 1 } },
];

/** Bare RFQ URL — resolves to the RFQ's current step. */
export const rfqPath = (rfqId: string): string => `/rfqs/${rfqIdForUrl(rfqId)}`;

/** Outreach thread URL — the thread id is a route param, not local state. */
export const outreachThreadPath = (rfqId: string, threadId: string): string =>
  `/rfqs/${rfqIdForUrl(rfqId)}/outreach/${encodeURIComponent(threadId)}`;

/** URL for a wizard position; out-of-range positions clamp to a live step. */
export const wizardStepPath = (rfqId: string, pos: WizardPos): string => {
  const { step, subStep } = pos;
  let segment: string;
  if (step <= 1) segment = 'upload';
  else if (step === 2) segment = 'criteria';
  else if (step === 3)
    segment = subStep <= 1 ? 'discovery/context' : subStep === 2 ? 'discovery/validation' : 'discovery';
  else if (step === 4) segment = 'outreach';
  else segment = 'quotes';
  return `/rfqs/${rfqIdForUrl(rfqId)}/${segment}`;
};
